import React, { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, ListChecks } from "lucide-react";
import { Button, Input, Textarea, Select, Checkbox, Card, CardContent, Modal, StatusBadge, LoadingBlock, EmptyState, toast } from "@/components/ds";
import { api, formatApiError } from "@/lib/api";
import { QUESTION_TYPES } from "./constants";
import { Field } from "../onboarding/_ui";

const EMPTY = { label: "", question_type: "short_text", help_text: "", options: "", required: false, active: true, order: "0" };

export function IntakeQuestionsPanel() {
  const [rows, setRows] = useState<Record<string, unknown>[] | null>(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Record<string, unknown> | null>(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [saving, setSaving] = useState(false);
  const set = (k: string, v: unknown) => setForm((f) => ({ ...f, [k]: v }));

  const load = () => api.intakeList().then(setRows).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, order: String(rows?.length ?? 0) }); setOpen(true); };
  const openEdit = (q: Record<string, unknown>) => {
    setEditing(q);
    setForm({
      label: (q.label as string) ?? "", question_type: (q.question_type as string) ?? "short_text",
      help_text: (q.help_text as string) ?? "", options: ((q.options as string[]) ?? []).join(", "),
      required: (q.required as boolean) ?? false, active: (q.active as boolean) ?? true, order: String(q.order ?? 0),
    });
    setOpen(true);
  };

  const save = async () => {
    if (form.label.trim().length < 1) { toast.error("Question label is required."); return; }
    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        label: form.label.trim(), question_type: form.question_type, help_text: form.help_text.trim() || null,
        options: form.question_type === "single_select" ? form.options.split(",").map((o) => o.trim()).filter(Boolean) : [],
        required: form.required, active: form.active, order: Number(form.order) || 0,
      };
      if (editing) await api.intakeUpdate(editing.id as string, body); else await api.intakeCreate(body);
      toast.success("Question saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const remove = async (id: string) => { try { await api.intakeDelete(id); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const typeLabel = (t: string) => QUESTION_TYPES.find((x) => x.value === t)?.label ?? t;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Qualification questions your AI asks before booking.</p>
        <Button onClick={openNew} data-testid="iq-add"><Plus className="h-4 w-4" /> Add question</Button>
      </div>

      {rows === null ? <LoadingBlock rows={3} /> : rows.length === 0 ? (
        <EmptyState icon={ListChecks} title="No intake questions" description="Add questions to qualify leads before booking."
          action={<Button onClick={openNew} data-testid="iq-empty-add"><Plus className="h-4 w-4" /> Add one</Button>} />
      ) : (
        <Card><CardContent className="divide-y divide-slate-100 p-0" data-testid="iq-list">
          {rows.map((q) => (
            <div key={q.id as string} className="flex items-center justify-between px-5 py-4" data-testid={`iq-item-${q.id}`}>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-mono text-slate-400">#{q.order as number}</span>
                  <p className="truncate text-sm font-medium text-slate-900">{q.label as string}</p>
                  {(q.required as boolean) && <StatusBadge tone="amber">Required</StatusBadge>}
                  {!(q.active as boolean) && <StatusBadge tone="slate">Inactive</StatusBadge>}
                </div>
                <p className="mt-0.5 text-xs text-slate-500">{typeLabel(q.question_type as string)}</p>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => openEdit(q)} data-testid={`iq-edit-${q.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button variant="ghost" size="sm" onClick={() => remove(q.id as string)} data-testid={`iq-delete-${q.id}`}><Trash2 className="h-4 w-4 text-slate-400" /></Button>
              </div>
            </div>
          ))}
        </CardContent></Card>
      )}

      <Modal open={open} onOpenChange={setOpen} title={editing ? "Edit question" : "Add question"} description="A qualification question asked before booking." testid="iq-modal">
        <div className="space-y-4">
          <Field label="Question" required testid="field-iq-label"><Input value={form.label} onChange={(e) => set("label", e.target.value)} data-testid="iq-label" /></Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Answer type" testid="field-iq-type"><Select value={form.question_type} onChange={(v) => set("question_type", v)} options={QUESTION_TYPES} testid="iq-type" /></Field>
            <Field label="Order" testid="field-iq-order"><Input type="number" value={form.order} onChange={(e) => set("order", e.target.value)} data-testid="iq-order" /></Field>
          </div>
          {form.question_type === "single_select" && (
            <Field label="Options (comma-separated)" testid="field-iq-options"><Input value={form.options} onChange={(e) => set("options", e.target.value)} placeholder="Yes, No, Maybe" data-testid="iq-options" /></Field>
          )}
          <Field label="Help text" testid="field-iq-help"><Textarea value={form.help_text} onChange={(e) => set("help_text", e.target.value)} data-testid="iq-help" /></Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex items-center gap-2 rounded-lg border border-slate-200 p-3"><Checkbox checked={form.required} onChange={(v) => set("required", v)} testid="iq-required" /><span className="text-sm text-slate-700">Required</span></label>
            <label className="flex items-center gap-2 rounded-lg border border-slate-200 p-3"><Checkbox checked={form.active} onChange={(v) => set("active", v)} testid="iq-active" /><span className="text-sm text-slate-700">Active</span></label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="iq-save">{saving ? "Saving…" : "Save"}</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
